import { match } from 'utils/pattern-match-util'
import BillingMethod from 'billing/models/BillingMethod'
import DataLoader from 'dataloader'
import { delay } from 'fejl'
import { PrimeMember, PrimeMemberByPublicId } from 'iam/MemberStore'
import Team from 'iam/models/Team'
import { GetCurrentMember } from 'iam/SessionStore'
import { GetTeamMember, GetTeamMembers } from 'iam/TeamMemberStore'
import { PrimeTeam } from 'iam/TeamStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { PrimeConversation } from 'messaging/ConversationStore'
import Message from 'messaging/models/Message'
import { action, computed, makeObservable } from 'mobx'
import { merge, Observable, Subject } from 'rxjs'
import { filter, map, share } from 'rxjs/operators'
import {
  CursorPaged,
  CursorPageQuery,
  EncodedCursor,
  Id,
  PrimeProps,
} from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { filterNonNil } from 'utils/rx-util'
import { LegendVersionId } from 'legends/PublishedLegend'
import { PrimePublishedLegend } from 'legends/PublishedLegendStore'
import { FeathersEvent, WorkAPI } from './API'
import { JobDTO, JobProtoDTO } from './dtos/job'
import { JobEventDTO, JobEventType, JobEventVisibility } from './dtos/job-event'
import { AnswerDTO } from './dtos/questions'
import Job, { AmendmentVisibility, Collaborator } from './Job'
import JobEvent from './JobEvent'
import { SynthesizedJobUpdates } from './SynthesizedJobUpdates'
import {
  CloseJobRequest,
  ForfeitJobRequest,
  GetJobRequest,
  PlaceJobOnHoldRequest,
  RenameJobRequest,
  ResumeJobRequest,
  AmendmentVisibility as AmendmentVisibilityProto,
  ClaimJobRequest,
  CopyJobRequest,
  CompleteInfoGatheringRequest,
  RequestJobChampionProviderRequest,
  RemoveSupportingClientRequest,
  AddTeamMemberAsSupportingClientRequest,
  BulkCopyJobResponse,
  BulkCopyJobRequest,
  ConsultationIncompleteReason as ConsultationIncompleteReasonProto,
  ConsultationIncomplete,
  ConsultationOtherIncompleteReason,
  ConsultationPresetIncompleteReason,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  CreateJobScopeChangeRequest,
  ListJobScopeChangesForCollaboratorRequest,
  JobScopeUpdateRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import {
  ISOToTimestamp,
  numberToDecimal,
  timestampToISO,
} from 'utils/grpc-util'
import { mapGrpcJobStatus, mapJobProtoToJobDTO } from './dtos/jobGrpcDtoMapper'
import {
  AddChampionProviderRequest,
  AddSupportingProviderRequest,
  CancelJobRequest,
  CloseJobAsTestRequest,
  CompleteJobRequest,
  CreateAmendmentRequest,
  RemoveClientTeamFromJobRequest,
  RemoveJobCollaboratorRequest,
  ReopenJobRequest,
  TransferJobToClientRequest,
  TransferJobToClientTeamRequest,
  TransferJobToPoolRequest,
  TransferJobToProviderRequest,
  ManuallySplitJobPayoutRequest,
  Allocation,
  ArchiveJobRequest,
  RemoveCouponFromJobRequest,
  ApplyCouponToJobRequest,
  AddInvoiceRequest,
  OverrideDeadlineRequest,
  ChargeRequest,
  RefundRequest,
  AddSupportingClientRequest,
  NotifyProvidersOfOpenJobRequest,
  BulkNotifyProvidersOfOpenJobsRequest,
  BulkArchiveJobsRequest,
  BulkRestoreJobsRequest,
  BulkTransferJobsToPoolRequest,
  BulkTransferJobsToClientTeamRequest,
  BulkTransferJobsToClientRequest,
  BulkTransferJobsToProviderRequest,
  BulkTransferJobsResponse,
} from '@taxfyle/api-internal/internal/job_admin_pb'
import {
  JobScopeChangeItemDTO,
  JobScopeChangeRequestDTO,
  mapProtoToJobScopeChangeStatusDto,
} from './dtos/job-scope-change'
import JobScopeChangeRequest from './JobScopeChange'
import { PrimeUser } from '../iam/UserStore'
import User from '../iam/models/User'
import { Decimal } from '@taxfyle/api-internal/shared/types/decimal_pb'
import { FetchBillingTransactions } from '../billing/BillingStore'
import {
  SubmitDraftJobRequest,
  GetJobSubmissionProgressRequest,
  RequestChampionProviderRequest,
  ApplyCouponRequest,
  RemoveCouponRequest,
  UpgradeLegendRequest,
  CreateDraftJobRequest,
  CreateDraftJobForTeamRequest,
  ArchiveDraftRequest,
} from '@taxfyle/api-internal/internal/draft_pb'
import {
  CallCenterApplyCouponRequest,
  CallCenterRemoveCouponRequest,
} from '@taxfyle/api-internal/internal/call_center_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { StatusCode } from 'grpc-web'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import TypeCase = RealtimeEvent.TypeCase
import {
  ListJobEventsRequest,
  JobEvent as JobEventProto,
} from '@taxfyle/api-internal/internal/job_event_pb'
import { difference } from 'lodash'
import { PrimeRoom } from 'messaging/rooms/RoomStore'
import { ConsultationIncompleteReason } from './consultations/Consultation'

export type GetJob = (id: Id) => Job | undefined
export type FetchJob = (id: Id) => Promise<Job>
export type PrimeJob = (props: PrimeProps<JobDTO, 'id'>) => Job | undefined
export type CanRemoveCollaborator = (collaborator: Collaborator) => boolean
export type PickupJob = (job: Job, team: Team) => Promise<PickupResult>

export const enum PickupResult {
  Success,
  Vanished,
  MaxConcurrentJobs,
}

/**
 * Delegate for providing payment info for a Job.
 */
export type ProvidePaymentInfoDelegate = (
  job: Job,
  paymentMethod: BillingMethod,
) => Promise<void>

export interface CopyJobResponse {
  jobId: Id
  readyForSubmission: boolean
}

export interface ConsultationIncompletePayload {
  reason: ConsultationIncompleteReason
  otherReason?: string
}

/**
 * Delegate for copying a job.
 */
export type CopyJob = (job: Job) => Promise<CopyJobResponse>

/**
 * Delegate for bulk copy jobs.
 */
export type BulkCopyJobs = (jobIds: Id[]) => Promise<BulkCopyJobResponse>

/**
 * Job Store.
 */
export default class JobStore {
  /**
   * Jobs collection.
   */
  jobs = collection<JobDTO, Job>({
    create: () =>
      new Job(
        this.primeWorkspace,
        this.primeUser,
        this.primeConversation,
        this.primeTeam,
        this.primePublishedLegend,
        this.getTeamMember,
        this.getTeamMembers,
        this.getCurrentMember,
        this.primeMemberByPublicId,
        this.primeRoom,
      ),
  })

  /**
   * Job events collection. We keep it here rather than
   * on the job because Job Event will attempt to hydrate a job.
   */
  jobEvents = collection<JobEventDTO, JobEvent>({
    create: (attrs) =>
      new JobEvent(this.primeJob({ id: attrs.job_id }), this.primeMember),
  })

  jobScopeChanges = collection<JobScopeChangeRequestDTO, JobScopeChangeRequest>(
    {
      create: (attrs) =>
        new JobScopeChangeRequest(this.primeJob({ id: attrs.job_id })),
    },
  )

  /**
   * Initialize DataLoader for jobs.
   */
  jobLoader = new DataLoader(this._fetchJobs.bind(this))

  /**
   * Get a single job by ID from the local collection.
   */
  getJob: GetJob = this.jobs.get

  /**
   * Primes a job.
   */
  primeJob: PrimeJob = this.jobs.set

  /**
   * Job updates observable. Emits an event every time the server tells
   * us about updates.
   */
  jobUpdates$ = merge(
    this.api.jobsV3.events$.pipe(
      map((realtimeEvent) => this._handleRemoteJobUpdateV3(realtimeEvent)),
      filterNonNil(),
    ),
    SynthesizedJobUpdates(this.newMessages$, this.fetchJob.bind(this)),
  ).pipe(share())

  /**
   * JobEvents observable. When realtime JobEvents are emitted, they flow through here.
   */
  jobEvents$ = merge(
    this.api.jobEventsV3.events$.pipe(map(this._handleRemoteJobEventRecorded)),
  ).pipe(filterNonNil(), map(this.jobEvents.set), share())

  /**
   * Local job changes subject.
   */
  private _localJobChangesSubject = new Subject<Job>()

  /**
   * Emits jobs that have been updated by actions taken locally.
   */
  readonly localJobChanges$ = this._localJobChangesSubject.asObservable()

  /**
   * Emits job updated events from Dotnet API.
   * TODO: Consolidate these.
   */
  readonly jobUpdatedEvents$: Observable<JobUpdatedEvent> = this.events$.pipe(
    filter((event) => event.getTypeCase() === TypeCase.JOB_UPDATED),
    map((e) => this.mapJobUpdatedRealtimeEvent(e)),
    filterNonNil(),
    share(),
  )

  readonly adminJobScopeUpdatedEvents$: Observable<AdminJobScopeUpdatedEvent> =
    this.events$.pipe(
      filter(
        (event) => event.getTypeCase() === TypeCase.ADMIN_JOB_SCOPE_UPDATED,
      ),
      map((e) => this.mapAdminJobScopeUpdatedRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

  readonly jobScopeChangeApproved$: Observable<JobScopeChangeApprovedEvent> =
    this.events$.pipe(
      filter(
        (event) => event.getTypeCase() === TypeCase.JOB_SCOPE_CHANGE_APPROVED,
      ),
      map((e) => this.mapJobScopeChangeApprovedRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

  constructor(
    private api: WorkAPI,
    private getCurrentMember: GetCurrentMember,
    private getTeamMember: GetTeamMember,
    private getTeamMembers: GetTeamMembers,
    private primeConversation: PrimeConversation,
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeMember: PrimeMember,
    private primeTeam: PrimeTeam,
    private primePublishedLegend: PrimePublishedLegend,
    private newMessages$: Observable<Message>,
    private fetchBillingTransactions: FetchBillingTransactions,
    private events$: Observable<RealtimeEvent>,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private primeRoom: PrimeRoom,
  ) {
    this.fetchJob = this.fetchJob.bind(this)
    this.refreshJob = this.refreshJob.bind(this)
    makeObservable(this)
    merge(
      this.jobUpdates$,
      this.jobEvents$,
      this.jobUpdatedEvents$,
      this.adminJobScopeUpdatedEvents$,
    ).subscribe()
    // Clear the loader cache and in-mem model when a Job is removed.
    this.jobUpdates$
      .pipe(filter((e) => e.event === 'removed'))
      .subscribe((e) => {
        this.jobs.remove(e.data.id!)
        this.jobLoader.clear(e.data.id!)
      })

    this.adminJobScopeUpdatedEvents$.subscribe((e) => {
      this.refreshJob(e.jobId)
      this.listScopeChangesForCollaborator(e.jobId)
    })
  }

  /**
   * Jobs available for pickup.
   */
  @computed
  get opportunities() {
    return this.jobs.filter((x) => x.status === 'UNCLAIMED')
  }

  /**
   * Open jobs.
   */
  @computed
  get currentJobs() {
    const userId = this.getCurrentMember().user.id
    return this.jobs.filter(
      (job) =>
        job.status === 'CLAIMED' &&
        job.collaborators.some((m) => m.member && m.member.user.id === userId),
    )
  }

  /**
   * Completed jobs.
   */
  @computed
  get completedJobs() {
    return this.jobs.filter((x) => x.status === 'CLOSED')
  }

  /**
   * Looks up events by Job.
   */
  eventsForJob = (job: Job) => this.jobEvents.filter((e) => e.job === job)

  /**
   * Looks up scope changes by Job.
   */
  scopeChangesForJob = (job: Job) =>
    this.jobScopeChanges.filter((e) => e.job === job)

  /**
   * Whether the scope changes are settled.
   */
  scopeChangesSettled = (job: Job) => {
    const scopeChanges = this.scopeChangesForJob(job)

    if (!scopeChanges.length) {
      return true
    }

    return scopeChanges.every(
      (scopeChange) =>
        scopeChange.status === 'Accepted' ||
        scopeChange.status === 'Rejected' ||
        scopeChange.status === 'ApplyFailed',
    )
  }

  /**
   * Fetch single job
   */
  fetchJob(id: Id): Promise<Job> {
    return this.jobLoader.load(Id.assert(id))
  }

  /**
   * Fetch single job without data loader
   */
  async refreshJob(id: Id): Promise<Job> {
    return await this._fetchJob(Id.assert(id)).then(this.jobs.set)
  }

  /**
   * Fetch many jobs
   */
  async fetchJobs(ids: Array<string>): Promise<Array<Job>> {
    return Promise.all(ids.map((id) => this.jobLoader.load(id)))
  }

  /**
   * Fetches job events.
   */
  async fetchJobEvents(
    jobId: Id,
    limit?: number,
    after?: EncodedCursor,
  ): Promise<CursorPaged<JobEvent>> {
    return this.api.jobEventsV3
      .list(constructRequest(Id.assert(jobId), limit, after))
      .then((result) => ({
        data: this.jobEvents.set(
          result
            .getJobEventsList()
            .map((x) => mapJobEventProtoToJobEventDto(x.toObject())),
        ),
        cursor: result.getNextPageToken(),
      }))

    function constructRequest(
      jobId: string,
      limit?: number,
      after?: EncodedCursor,
    ) {
      const request = new ListJobEventsRequest().setJobId(jobId)

      if (limit) {
        request.setPageSize(limit)
      }

      if (after) {
        request.setPageToken(after)
      }

      return request
    }
  }

  /**
   * Creates a Draft Job.
   */
  async createDraftJob(
    legendVersionId: LegendVersionId,
    clientUserPublicId: Id,
  ): Promise<Job> {
    const request = new CreateDraftJobRequest()

    request.setLegendId(legendVersionId.id)
    request.setLegendVersion(legendVersionId.version)
    request.setClientUserPublicId(Id.assert(clientUserPublicId))

    const draft = await this.api.drafts.createDraftJob(request)
    return this._fetchJob(Id.assert(draft.toObject().id)).then(this.jobs.set)
  }

  /**
   * Creates a Team Draft Job.
   */
  async createTeamDraftJob(
    legendVersionId: LegendVersionId,
    teamId: Id,
    clientUserPublicId: Id,
  ): Promise<Job> {
    const request = new CreateDraftJobForTeamRequest()

    request.setLegendId(legendVersionId.id)
    request.setLegendVersion(legendVersionId.version)
    request.setClientUserPublicId(Id.assert(clientUserPublicId))
    request.setTeamId(Id.assert(teamId))

    const draft = await this.api.drafts.createDraftJobForTeam(request)
    return this._fetchJob(Id.assert(draft.toObject().id)).then(this.jobs.set)
  }

  /**
   * Updates the Legend Version for a Draft Job.
   *
   * @param jobId
   */
  async updateDraftLegendVersion(jobId: Id) {
    const request = new UpgradeLegendRequest()
    request.setId(Id.assert(jobId))
    const draft = await this.api.drafts.upgradeLegend(request)
    return this._fetchJob(Id.assert(draft.toObject().id)).then(this.jobs.set)
  }

  /**
   * Notifies pros of the availability of the given job.
   */
  async notifyProsOfOpenJob(job: Job) {
    await this.api.jobsV3Admin.notifyProsOfOpenJob(
      new NotifyProvidersOfOpenJobRequest().setId(Id.assert(job.id)),
    )
  }

  /**
   * Bulk notifies pros of all unclaimed jobs in a workspace.
   */
  async bulkNotifyProsOfOpenJobs(workspaceId: string) {
    const response = await this.api.jobsV3Admin.bulkNotifyProsOfOpenJobs(
      new BulkNotifyProvidersOfOpenJobsRequest().setWorkspaceId(
        Id.assert(workspaceId),
      ),
    )
    return response.getJobsNotified()
  }

  /**
   * Picks up a job.
   *
   * @param job
   * @param team
   */
  async pickup(job: Job): Promise<PickupResult> {
    return this.pickupInternal(job.id)
  }

  /**
   * We call this from the Ops store since currently Work API
   * still handles picking up ops.
   *
   * @param jobId
   * @param team
   */
  async pickupInternal(jobId: Id) {
    const claimJobRequest = new ClaimJobRequest().setId(Id.assert(jobId))

    try {
      await this.api.jobsV3.claimJob(claimJobRequest)
      const job = await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
      this._localJobChangesSubject.next(job)
      return PickupResult.Success
    } catch (error) {
      if (error.code === StatusCode.FAILED_PRECONDITION) {
        if (error.message.includes('JobAlreadyPickedUp')) {
          return PickupResult.Vanished
        }
      }

      throw error
    }
  }

  async copyJob(job: Job): Promise<CopyJobResponse> {
    const request = new CopyJobRequest().setId(Id.assert(job.id))
    const response = await this.api.jobsV3.copyJob(request)
    return {
      jobId: response.getId(),
      readyForSubmission: response.getReadyForSubmission(),
    }
  }

  async bulkCopyJobs(jobIds: Id[]): Promise<BulkCopyJobResponse> {
    const request = new BulkCopyJobRequest().setIdsList(
      jobIds.map((id) => Id.assert(id)),
    )
    return await this.api.jobsV3.bulkCopyJobs(request)
  }

  /**
   * Complete the info gathering step for a job
   */
  async completeInfoGathering(jobId: Id, completeAsAdmin: boolean = false) {
    const request = new CompleteInfoGatheringRequest()
      .setId(Id.assert(jobId))
      .setCompleteAsAdmin(completeAsAdmin)
    await this.api.jobsV3.completeInfoGathering(request)
    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  /**
   * Renames a job.
   *
   * @param job
   * @param newName
   */
  async rename(job: Job, newName: string) {
    const req = new RenameJobRequest().setId(Id.assert(job.id)).setName(newName)

    const renameJobResponse = (await this.api.jobsV3.renameJob(req)).toObject()
    const dateModified = timestampToISO(renameJobResponse.updateTime)

    const updatedJob = this.jobs.set({
      id: job.id,
      name: renameJobResponse.name,
      description: job.description,
      date_modified: dateModified,
    })

    this._localJobChangesSubject.next(updatedJob)

    return updatedJob
  }

  async placeJobOnHold(id: string) {
    const request = new PlaceJobOnHoldRequest().setId(id)
    const placeJobOnHoldResponse = (
      await this.api.jobsV3.placeJobOnHold(request)
    ).toObject()
    const dateModified = timestampToISO(placeJobOnHoldResponse.updateTime)
    return this.jobs.set({
      id,
      status: mapGrpcJobStatus(placeJobOnHoldResponse.status),
      date_modified: dateModified,
    })
  }

  async deleteDraftAsCollaborator(job: Job) {
    const jobId = Id.assert(job.id)

    const request = new ArchiveDraftRequest().setId(jobId)

    await this.api.drafts.deleteDraftJob(request)

    this.jobs.remove(jobId)
  }

  async deleteJobAsAdmin(job: Job) {
    const jobId = Id.assert(job.id)

    const request = new ArchiveJobRequest().setId(jobId)

    await this.api.jobsV3Admin.archiveJob(request)

    this.jobs.remove(jobId)
  }

  async addManualInvoice(
    jobId: Id,
    invoiceId: string,
    amount: number,
    reason: string,
    isDraft: boolean,
  ) {
    const request = new AddInvoiceRequest()
      .setId(Id.assert(jobId))
      .setChargeTransactionId(invoiceId)
      .setAmount(numberToDecimal(amount))
      .setReason(reason)

    if (isDraft) {
      await this.api.draftsAdmin.addInvoice(request)
    } else {
      await this.api.jobsV3Admin.addInvoice(request)
    }

    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  /**
   * Charges a job with the specified payment method and amount.
   */
  async chargeJob(
    jobId: Id,
    paymentMethodId: Id,
    amount: number,
    reason: string,
  ) {
    const request = new ChargeRequest()
      .setId(Id.assert(jobId))
      .setAmount(numberToDecimal(amount))
      .setReason(reason)
      .setBillingMethodId(Id.assert(paymentMethodId))

    await this.api.jobsV3Admin.chargeJob(request)

    const updated = await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
    await this.fetchBillingTransactions(updated.stripeCharges)
  }

  /**
   * Refunds a job with the specified amount.
   */
  async refundJob(jobId: Id, amount: number, reason: string) {
    const request = new RefundRequest()
      .setId(Id.assert(jobId))
      .setAmount(numberToDecimal(amount))
      .setReason(reason)

    await this.api.jobsV3Admin.refundJob(request)

    const updated = await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
    await this.fetchBillingTransactions([
      ...updated.stripeCharges,
      ...updated.stripeRefunds,
    ])
  }

  async updateDeadline(jobId: Id, deadline: Date) {
    const request = new OverrideDeadlineRequest()
      .setJobId(Id.assert(jobId))
      .setDeadline(ISOToTimestamp(deadline.toISOString()))

    await this.api.jobsV3Admin.overrideDeadline(request)

    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  async resumeJob(id: string) {
    const request = new ResumeJobRequest().setId(id)
    const resumeJobResponse = (
      await this.api.jobsV3.resumeJob(request)
    ).toObject()
    const dateModified = timestampToISO(resumeJobResponse.updateTime)
    return this.jobs.set({
      id,
      status: mapGrpcJobStatus(resumeJobResponse.status),
      date_modified: dateModified,
    })
  }

  /**
   * Creates an amendment to the job.
   *
   * @param id
   * @param description
   * @param internalNote
   * @param visibility
   * @param clientAmount
   * @param providerAmount
   */
  createAmendment(
    id: string,
    description: string,
    internalNote: string,
    visibility: AmendmentVisibility,
    clientAmount: Decimal,
    providerAmount: Decimal,
  ) {
    const request = new CreateAmendmentRequest()
      .setId(id)
      .setDescription(description)
      .setInternalNote(internalNote)
      .setVisibility(
        match(visibility, {
          [AmendmentVisibility.Public]: () => AmendmentVisibilityProto.PUBLIC,
          [AmendmentVisibility.Internal]: () =>
            AmendmentVisibilityProto.INTERNAL,
        }),
      )
      .setCustomerAmount(clientAmount)
      .setProviderAmount(providerAmount)

    return this.api.jobsV3Admin
      .createAmendment(request)
      .then(() => this.updateJobEvents(id))
  }

  async transferJobToPoolAsAdmin(id: string, reason: string) {
    const request = new TransferJobToPoolRequest().setId(id).setReason(reason)

    return this.api.jobsV3Admin
      .transferJobToPool(request)
      .then(() => this.updateJobEvents(id))
  }

  async bulkTransferJobsToPool(ids: string[], reason: string) {
    const request = new BulkTransferJobsToPoolRequest()
      .setIdsList(ids)
      .setReason(reason)

    const response = await this.api.jobsV3Admin.bulkTransferJobToPool(request)

    return mapBulkTransferProtoResponseToDto(response.toObject())
  }

  transferJobToClientAsAdmin(
    id: string,
    clientUserPublicId: string,
    reason: string,
  ) {
    const request = new TransferJobToClientRequest()
      .setId(id)
      .setClientUserPublicId(clientUserPublicId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .transferJobToClient(request)
      .then(() => this.updateJobEvents(id))
  }

  async bulkTransferJobsToClientAsAdmin(
    ids: string[],
    clientId: string,
    reason: string,
  ) {
    const request = new BulkTransferJobsToClientRequest()
      .setIdsList(ids)
      .setClientUserPublicId(clientId)
      .setReason(reason)

    const response =
      await this.api.jobsV3Admin.bulkTransferJobsToClient(request)

    return mapBulkTransferProtoResponseToDto(response.toObject())
  }

  transferJobToProAsAdmin(
    id: string,
    providerUserPublicId: string,
    reason: string,
  ) {
    const request = new TransferJobToProviderRequest()
      .setId(id)
      .setProviderUserPublicId(providerUserPublicId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .transferJobToPro(request)
      .then(() => this.updateJobEvents(id))
  }

  async bulkTransferJobsToProviderAsAdmin(
    ids: string[],
    providerId: string,
    reason: string,
  ) {
    const request = new BulkTransferJobsToProviderRequest()
      .setIdsList(ids)
      .setProviderUserPublicId(providerId)
      .setReason(reason)

    const response =
      await this.api.jobsV3Admin.bulkTransferJobsToProvider(request)

    return mapBulkTransferProtoResponseToDto(response.toObject())
  }

  async bulkTransferJobToClientTeamAsAdmin(
    ids: string[],
    teamId: string,
    reason: string,
  ) {
    const request = new BulkTransferJobsToClientTeamRequest()
      .setIdsList(ids)
      .setTeamId(teamId)
      .setReason(reason)

    const response =
      await this.api.jobsV3Admin.bulkTransferJobToClientTeam(request)

    return mapBulkTransferProtoResponseToDto(response.toObject())
  }

  transferJobToClientTeamAsAdmin(id: string, teamId: string, reason: string) {
    const request = new TransferJobToClientTeamRequest()
      .setId(id)
      .setTeamId(teamId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .transferJobToClientTeam(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Requests a champion Pro to a job as Admin.
   *
   * @param id
   * @param championProviderId
   */
  async requestChampionProviderAsAdmin(id: Id, championProviderId: Id | null) {
    const request = new RequestJobChampionProviderRequest().setId(Id.assert(id))

    if (championProviderId) {
      const championProviderIdValue = new StringValue()
      championProviderIdValue.setValue(Id.assert(championProviderId))

      request.setProviderUserPublicId(championProviderIdValue)
    }

    return this.api.jobsV3Admin
      .requestJobChampionProvider(request)
      .then(() => this.updateJobEvents(Id.assert(id)))
  }

  /**
   * Adds a champion Provider to a job as Admin.
   *
   * @param id
   * @param providerUserPublicId
   * @param reason
   */
  addChampionProviderAsAdmin(
    id: string,
    providerUserPublicId: string,
    reason: string,
  ) {
    const request = new AddChampionProviderRequest()
      .setId(id)
      .setProviderUserPublicId(providerUserPublicId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .addJobChampionProvider(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Adds a supporting Provider to a job as Admin.
   *
   * @param id
   * @param providerUserPublicId
   * @param reason
   */
  addSupportingProviderAsAdmin(
    id: string,
    providerUserPublicId: string,
    reason: string,
  ) {
    const request = new AddSupportingProviderRequest()
      .setId(id)
      .setProviderUserPublicId(providerUserPublicId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .addJobSupportingProvider(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Adds a supporting Client to a job as Admin.
   *
   * @param id
   * @param clientUserPublicId
   * @param reason
   */
  addSupportingClientAsAdmin(
    id: string,
    clientUserPublicId: string,
    reason: string,
  ) {
    const request = new AddSupportingClientRequest()
      .setId(id)
      .setClientUserPublicId(clientUserPublicId)
      .setReason(reason)

    return this.api.jobsV3Admin
      .addJobSupportingClient(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Reopens a job as an Admin.
   *
   * @param id
   * @param collaboratorUserPublicId
   * @param reason
   */
  async reopenJobAsAdmin(id: string, reason: string) {
    const request = new ReopenJobRequest().setId(id).setReason(reason)

    return this.api.jobsV3Admin
      .reopenJob(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Removes client team from Job as Admin.
   *
   * @param id
   * @param reason
   */
  async removeClientTeamAsAdmin(id: string, reason: string) {
    const request = new RemoveClientTeamFromJobRequest()
      .setId(id)
      .setReason(reason)

    await this.api.jobsV3Admin.removeClientTeamFromJob(request)

    await Promise.all([
      this._fetchJob(id).then(this.jobs.set),
      this.updateJobEvents(id),
    ])
  }

  /**
   * Removes a collaborator from a job as Admin.
   *
   * @param id
   * @param collaboratorUserPublicId
   * @param reason
   */
  async removeCollaboratorAsAdmin(
    id: string,
    collaboratorUserPublicId: string,
    reason: string,
  ) {
    const request = new RemoveJobCollaboratorRequest()
      .setId(id)
      .setCollaboratorUserPublicId(collaboratorUserPublicId)
      .setReason(reason)

    await this.api.jobsV3Admin.removeJobCollaborator(request)

    return this._fetchJob(id).then(this.jobs.set)
  }

  /**
   * Removes a supporting client from a job as either champion client or team member.
   *
   * @param id
   * @param collaboratorUserPublicId
   */
  async removeClientAsNonAdmin(id: string, collaboratorUserPublicId: string) {
    const request = new RemoveSupportingClientRequest()
      .setId(id)
      .setClientUserPublicId(collaboratorUserPublicId)

    await this.api.jobsV3.removeSupportingClient(request)
    return this._fetchJob(id).then(this.jobs.set)
  }

  /**
   * Add a team member as a supporting client to the job.
   *
   * @param id
   * @param userPublicId
   */
  async addTeamMemberAsSupportingClient(id: string, userPublicId: string) {
    const request = new AddTeamMemberAsSupportingClientRequest()
      .setId(id)
      .setUserPublicId(userPublicId)

    await this.api.jobsV3.addTeamMemberAsSupportingClient(request)
    return this._fetchJob(id).then(this.jobs.set)
  }

  /**
   * Closes a job.
   *
   * @param job
   * @param payload
   */
  async close(
    job: Job,
    consultationIncomplete?: ConsultationIncompletePayload,
  ) {
    const request = new CloseJobRequest().setId(Id.assert(job.id))

    if (consultationIncomplete) {
      const mappedConsultationIncomplete =
        mapConsultationIncompleteReasonFromPayload(consultationIncomplete)

      if (typeof mappedConsultationIncomplete === 'string') {
        request.setConsultationIncomplete(
          new ConsultationIncomplete().setOtherIncompleteReason(
            new ConsultationOtherIncompleteReason().setReason(
              mappedConsultationIncomplete,
            ),
          ),
        )
      } else if (mappedConsultationIncomplete) {
        request.setConsultationIncomplete(
          new ConsultationIncomplete().setIncompleteReason(
            new ConsultationPresetIncompleteReason().setReason(
              mappedConsultationIncomplete,
            ),
          ),
        )
      }
    }

    await this.api.jobsV3.closeJobAsPro(request)

    const updated = await this._fetchJob(Id.assert(job.id)).then(this.jobs.set)

    await this.fetchBillingTransactions(updated.stripeCharges)

    return updated
  }

  /**
   * Completes a job.
   *
   * @param id
   * @param reason
   * @param doBilling
   * @param billingMethodId
   */
  async complete(
    id: string,
    reason: string,
    doBilling: boolean,
    billingMethodId: string,
  ) {
    const request = new CompleteJobRequest()
      .setId(Id.assert(id))
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(billingMethodId)

    await this.api.jobsV3Admin
      .completeJob(request)
      .then(() => this.updateJobEvents(id))

    const updated = await this._fetchJob(Id.assert(id)).then(this.jobs.set)

    await this.fetchBillingTransactions(updated.stripeCharges)

    return updated
  }

  /**
   * Cancels a job.
   *
   * @param id
   * @param reason
   * @param doBilling
   * @param billingMethodId
   */
  async cancel(
    id: string,
    reason: string,
    doBilling: boolean,
    billingMethodId: string,
  ) {
    const request = new CancelJobRequest()
      .setId(Id.assert(id))
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(billingMethodId)

    return this.api.jobsV3Admin
      .cancelJob(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Closes as a Test job.
   *
   * @param id
   * @param reason
   * @param doBilling
   * @param billingMethodId
   */
  closeJobAsTest(
    id: string,
    reason: string,
    doBilling: boolean,
    billingMethodId: string,
  ) {
    const request = new CloseJobAsTestRequest()
      .setId(Id.assert(id))
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(billingMethodId)

    return this.api.jobsV3Admin
      .closeJobAsTest(request)
      .then(() => this.updateJobEvents(id))
  }

  /**
   * Forfeits the job.
   *
   * @param job
   */
  async forfeit(job: Job) {
    const req = new ForfeitJobRequest().setId(Id.assert(job.id))

    await this.api.jobsV3.forfeitJob(req)

    return this._fetchJob(Id.assert(job.id)).then(this.jobs.set)
  }

  /**
   * Job Scope Update as an admin.
   *
   * @param job
   * @param reason
   * @param scopeChangeCustomItemsToCreate
   * @param scopeChangeCustomItemsToRemove
   * @param scopeChangeInventoryItems
   */
  adminJobScopeUpdate(
    job: Job,
    reason: string,
    scopeChangeCustomItemsToCreate: AdminJobScopeUpdateCustomItemToCreateParams[],
    scopeChangeCustomItemsToRemove: AdminJobScopeUpdateCustomItemToRemoveParams[],
    scopeChangeInventoryItems: AdminJobScopeUpdateInventoryItemParams[],
  ) {
    const customItemsToAddRequest = scopeChangeCustomItemsToCreate.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateCreateCustomItem()
        .setName(i.name)
        .setQuantity(i.quantity)
        .setPrice(numberToDecimal(i.price))
        .setProPrice(numberToDecimal(i.proPrice)),
    )

    const customItemsToRemoveRequest = scopeChangeCustomItemsToRemove.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateRemoveCustomItem().setId(
        i.custom_item_id,
      ),
    )

    const inventoryItemsRequest = scopeChangeInventoryItems.map((i) =>
      new JobScopeUpdateRequest.JobScopeUpdateInventoryItem()
        .setVersionId(i.versionId)
        .setQuantity(i.quantity),
    )

    const request = new JobScopeUpdateRequest()
      .setJobId(Id.assert(job.id))
      .setReason(reason)
      .setCustomItemsToAddList(customItemsToAddRequest)
      .setCustomItemsToRemoveList(customItemsToRemoveRequest)
      .setInventoryItemsList(inventoryItemsRequest)

    return this.api.scopeChangeRequests.updateJobScope(request)
  }

  /**
   * Request scope changes on a Job.
   *
   * @param job
   * @param reason
   * @param scopeChangeItems
   */
  requestScopeChanges(
    job: Job,
    reason: string,
    scopeChangeItems: ScopeChangeParams[],
    scopeChangeInventoryItems: ScopeChangeInventoryParams[],
  ) {
    const itemsRequest = scopeChangeItems.map((i) =>
      new CreateJobScopeChangeRequest.CreateJobScopeChangeItemRequest()
        .setName(i.name)
        .setQuantity(i.quantity),
    )

    const inventoryItemsRequest = scopeChangeInventoryItems.map((i) =>
      new CreateJobScopeChangeRequest.CreateJobScopeChangeInventoryItemRequest()
        .setVersionId(i.versionId)
        .setQuantity(i.quantity),
    )

    const request = new CreateJobScopeChangeRequest()
      .setJobId(Id.assert(job.id))
      .setReason(reason)
      .setItemsList(itemsRequest)
      .setInventoryItemsList(inventoryItemsRequest)

    return this.api.scopeChangeRequests.requestScopeChanges(request)
  }

  /**
   * List scope changes on a Job.
   *
   * @param job
   */
  async listScopeChangesForCollaborator(jobId: Id) {
    const request = new ListJobScopeChangesForCollaboratorRequest().setJobId(
      Id.assert(jobId),
    )

    return this.api.scopeChangeRequests
      .listScopeChangesForCollaborator(request)
      .then((result) =>
        result.getJobScopeChangesList().forEach((scopeChange) => {
          this.jobScopeChanges.set({
            id: scopeChange.getId(),
            job_id: scopeChange.getJobId(),
            status: mapProtoToJobScopeChangeStatusDto(scopeChange.getStatus()),
            items: scopeChange.getItemsList().map(
              (item) =>
                ({
                  id: item.getId(),
                  name: item.getName(),
                  quantity: item.getQuantity(),
                }) as JobScopeChangeItemDTO,
            ),
            rejectionReason: scopeChange.getRejectionReason(),
            workspace_id: scopeChange.getWorkspaceId(),
            created_by_user_public_id: scopeChange.getCreatedByUserPublicId(),
            create_time: scopeChange.getCreateTime()?.toDate(),
            latest_response_time: scopeChange.getLatestResponseTime()?.toDate(),
          })
        }),
      )
  }

  /** Requests a champion provider for a draft.
   *
   * @param {Job} draft.
   * @param {(Member|null)} championProvider. When null, any champion provider that
   * can be assigned to this job will be requested.
   */
  async requestChampionProvider(draft: Job, championProviderId: Id | null) {
    const request = new RequestChampionProviderRequest().setId(
      Id.assert(draft.id),
    )

    if (championProviderId) {
      const championProviderIdValue = new StringValue()
      championProviderIdValue.setValue(Id.assert(championProviderId))

      request.setProviderUserPublicId(championProviderIdValue)
    }

    await this.api.drafts.requestChampionProvider(request)

    await this._fetchJob(Id.assert(draft.id)).then(this.jobs.set)
  }

  /** Applies a coupon to a draft.
   *
   * @param {Job} draft.
   * @param {Id} couponId.
   */
  async applyCoupon(draft: Job, couponId: Id) {
    const request = new ApplyCouponRequest().setId(Id.assert(draft.id))

    const couponIdValue = new StringValue()
    couponIdValue.setValue(Id.assert(couponId))

    request.setCouponId(couponIdValue)

    await this.api.drafts.applyCoupon(request)

    await this._fetchJob(Id.assert(draft.id)).then(this.jobs.set)
  }

  /** Applies a coupon as an admin to a job.
   *
   * @param {Job} job.
   * @param {Id} couponId.
   */
  async applyCouponAsAdmin(job: Job, couponId: Id) {
    const request = new ApplyCouponToJobRequest()
      .setId(Id.assert(job.id))
      .setCouponId(Id.assert(couponId))

    await this.api.jobsV3Admin.applyCoupon(request)

    await this._fetchJob(Id.assert(job.id)).then(this.jobs.set)
  }

  async callCenterApplyCouponByCode(jobId: Id, couponCode: string) {
    const request = new CallCenterApplyCouponRequest()
      .setCouponCode(couponCode)
      .setDraftId(Id.assert(jobId))

    await this.api.callCenter.applyCoupon(request)

    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  async callCenterRemoveCouponByCode(jobId: Id) {
    const request = new CallCenterRemoveCouponRequest().setDraftId(
      Id.assert(jobId),
    )

    await this.api.callCenter.removeCoupon(request)

    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  /** Removes coupon from draft.
   *
   * @param {Job} draft.
   */
  async removeCoupon(draft: Job) {
    const request = new RemoveCouponRequest().setId(Id.assert(draft.id))

    await this.api.drafts.removeCoupon(request)

    await this._fetchJob(Id.assert(draft.id)).then(this.jobs.set)
  }

  /**
   * Upgrades the Legend Version for a Draft Job.
   *
   * @param job
   * @param params
   */
  async upgradeLegend(jobId: Id) {
    const request = new UpgradeLegendRequest().setId(Id.assert(jobId))

    await this.api.drafts.upgradeLegend(request)
    await this._fetchJob(Id.assert(jobId)).then(this.jobs.set)
  }

  /** Removes coupon as an admin from a job.
   *
   * @param {Job} job.
   */
  async removeCouponAsAdmin(job: Job) {
    const request = new RemoveCouponFromJobRequest().setId(Id.assert(job.id))

    await this.api.jobsV3Admin.removeCoupon(request)

    await this._fetchJob(Id.assert(job.id)).then(this.jobs.set)
  }

  /**
   * Submits using the V3 API.
   *
   * @param jobId
   * @param params
   */
  async submitRequestV3(
    jobId: Id,
    params: SubmitRequestParams,
    tryUpgradeLegend = false,
  ) {
    if (tryUpgradeLegend) {
      try {
        await this.updateDraftLegendVersion(jobId)
      } catch (e) {
        console.error('Failed to upgrade legend ', jobId)
      }
    }

    const submitRequest = new SubmitDraftJobRequest().setId(Id.assert(jobId))

    if (params.bypassBilling) {
      submitRequest.setBypassBilling(params.bypassBilling)
    }

    if (params.paymentMethod) {
      submitRequest.setBillingMethodId(
        new StringValue().setValue(Id.assert(params.paymentMethod.id)),
      )
    }
    // Submit the draft job; we will get back a submission process ID that we can poll.
    const submitResponse = await this.api.drafts.submitDraftJob(submitRequest)

    do {
      await delay(400)
      try {
        const progress = await this.api.drafts.getJobSubmissionProgress(
          new GetJobSubmissionProgressRequest().setSubmissionId(
            submitResponse.getSubmissionId(),
          ),
        )

        if (progress.getStatus()?.getCompleted()) {
          const job = await this._fetchJob(submitResponse.getId()).then(
            this.jobs.set,
          )

          await this.fetchBillingTransactions(job.stripeCharges)

          return job
        }

        const failed = progress.getStatus()?.getFailed()
        if (failed) {
          throw new Error(failed.getDescription())
        }
      } catch (err: any) {
        // The progress may not exist yet.
        if (err.code !== StatusCode.NOT_FOUND) {
          throw err
        }
      }
    } while (true)
  }

  /**
   * Splits the payout.
   *
   * @param job
   * @param allocations
   */
  async splitPayout(job: Job, allocations: AllocationInput[]) {
    const id = Id.assert(job.id)
    const allocationsDto = allocations.map((input) =>
      new Allocation()
        .setUserPublicId(Id.assert(input.user.publicId))
        .setAmount(numberToDecimal(input.allocationAmount))
        .setAuthorizedPartialPayoutAmount(
          numberToDecimal(input.authorizedPartialPayoutAmount),
        ),
    )
    const request = new ManuallySplitJobPayoutRequest()
      .setId(id)
      .setAllocationsList(allocationsDto)

    await this.api.jobsV3Admin.manuallySplitJobPayout(request)

    // Retrieve the updated job.
    await this._fetchJobs([id])
  }

  /**
   * Attempts to archive the given jobs.
   */
  async archiveJobs(jobIds: string[]) {
    const ids = jobIds.map(Id.assert)

    const response = await this.api.jobsV3Admin.bulkArchiveJobs(
      new BulkArchiveJobsRequest().setIdsList(ids),
    )

    const denied = response.getDeniedIdsList()
    const alreadyArchived = response.getAlreadyArchivedIdsList()
    const submissionInProgress = response.getSubmissionInProgressIdsList()
    const notFound = response.getNotFoundIdsList()
    const transactionsFound = response.getTransactionsFoundIdsList()
    const archived = difference(
      ids,
      denied
        .concat(alreadyArchived)
        .concat(submissionInProgress)
        .concat(transactionsFound)
        .concat(notFound),
    )

    return {
      denied,
      alreadyArchived,
      submissionInProgress,
      notFound,
      transactionsFound,
      archived,
    }
  }

  /**
   * Attempts to restore (meaning unarchive) the given jobs.
   */
  async restoreJobs(jobIds: string[]) {
    const ids = jobIds.map(Id.assert)

    const response = await this.api.jobsV3Admin.bulkRestoreJobs(
      new BulkRestoreJobsRequest().setIdsList(ids),
    )

    const denied = response.getDeniedIdsList()
    const submissionInProgress = response.getSubmissionInProgressIdsList()
    const notFound = response.getNotFoundIdsList()
    const restored = difference(
      ids,
      denied.concat(submissionInProgress).concat(notFound),
    )

    return {
      denied,
      submissionInProgress,
      notFound,
      restored,
    }
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private mapJobUpdatedRealtimeEvent(e: RealtimeEvent): JobUpdatedEvent | null {
    if (e.getTypeCase() !== TypeCase.JOB_UPDATED) {
      return null
    }

    const proto = e.getJobUpdated()?.getJob()?.toObject()
    if (!proto) {
      return null
    }

    const jobDto = mapJobProtoToJobDTO(proto as JobProtoDTO)

    // Dotnet API events are over-exposing information, for example
    // the milestone tasks are not filtered based on designee, so we can't
    // use all the data that's on there.
    // This is currently only used for the Insights feature which relies on
    // a signal for the job to have updated, so we'll just update the
    // Last Activity for now.
    const existing = this.jobs.get(jobDto.id)
    if (!existing) {
      return null
    }

    return {
      type: 'JobUpdated',
      job: this.jobs.set({
        id: jobDto.id,
        date_last_activity: jobDto.date_last_activity,
      }),
    }
  }

  private mapAdminJobScopeUpdatedRealtimeEvent(
    e: RealtimeEvent,
  ): AdminJobScopeUpdatedEvent | null {
    if (e.getTypeCase() !== TypeCase.ADMIN_JOB_SCOPE_UPDATED) {
      return null
    }

    const jobId = e.getAdminJobScopeUpdated()?.getJobId()
    if (!jobId) {
      return null
    }

    return {
      type: 'AdminJobScopeUpdated',
      jobId,
    }
  }

  private mapJobScopeChangeApprovedRealtimeEvent(
    e: RealtimeEvent,
  ): JobScopeChangeApprovedEvent | null {
    if (e.getTypeCase() !== TypeCase.JOB_SCOPE_CHANGE_APPROVED) {
      return null
    }

    const jobId = e.getJobScopeChangeApproved()?.getJobId()
    if (!jobId) {
      return null
    }

    return {
      type: 'JobScopeChangeApproved',
      jobId,
    }
  }

  /**
   * Used by DataLoader.
   */
  private async _fetchJobs(ids: readonly string[]) {
    // TODO: when API supports get-many, use that.
    return Promise.all(
      ids.map(async (id) =>
        this._fetchJob(id)
          .then(this.jobs.set)
          .catch((err) => {
            this.jobLoader.clear(id)
            throw err
          }),
      ),
    )
  }

  /**
   * Used to load a job
   */
  private async _fetchJob(id: string): Promise<Partial<JobDTO>> {
    const request = new GetJobRequest().setId(id)

    return this.api.jobsV3
      .getJob(request)
      .then((jobProto) =>
        mapJobProtoToJobDTO(jobProto.toObject() as JobProtoDTO),
      )
  }

  /**
   * Handles an incoming job update event.
   *
   * @param event
   */
  @action.bound
  private _handleRemoteJobUpdate(
    event: FeathersEvent<JobDTO>,
  ): FeathersEvent<Job> | undefined {
    switch (event.event) {
      case 'created':
      case 'patched': {
        return {
          ...event,
          data: this._mergeWithRemoteData(event.data),
        }
      }
      case 'removed': {
        const job = this._mergeWithRemoteData(event.data)
        this.jobs.remove(event.data.id)
        return {
          ...event,
          data: job,
        }
      }
    }
  }

  /**
   * Handles an incoming job update event from DotNET API.
   *
   * @param realtimeEvent
   */
  @action.bound
  private _handleRemoteJobUpdateV3(
    realtimeEvent: RealtimeEvent,
  ): FeathersEvent<Job> | undefined {
    const isJobUpdatedEvent = () =>
      realtimeEvent.getTypeCase() === RealtimeEvent.TypeCase.JOB_UPDATED

    const jobProtoDto = isJobUpdatedEvent()
      ? realtimeEvent.getJobUpdated()?.toObject()?.job
      : realtimeEvent.getJobCreated()?.toObject()?.job

    if (!jobProtoDto) {
      return
    }

    const jobDto = mapJobProtoToJobDTO(jobProtoDto as JobProtoDTO)
    const event = isJobUpdatedEvent() ? 'patched' : 'created'

    return this._handleRemoteJobUpdate({
      event: jobProtoDto.archived ? 'removed' : event,
      data: jobDto as JobDTO,
    })
  }

  /**
   * Handles an incoming job recorded event from DotNET API.
   *
   * @param realtimeEvent
   */
  @action.bound
  private _handleRemoteJobEventRecorded(
    realtimeEvent: RealtimeEvent,
  ): JobEventDTO | undefined {
    const jobEventProtoDto = realtimeEvent
      .getJobEventRecorded()
      ?.toObject()?.jobEvent

    if (!jobEventProtoDto) {
      return
    }

    return mapJobEventProtoToJobEventDto(jobEventProtoDto)
  }

  /**
   * Handles the realtime updates.
   *
   * @param data
   */
  @action.bound
  private _mergeWithRemoteData(data: JobDTO): Job {
    const job = this.jobs.get(data.id)
    if (!job) {
      return this.jobs.set(data)
    }
    const eventDate = maybeParseDate(data.date_modified) || DateZero()
    if (job.dateModified < eventDate) {
      return this.jobs.set(data)
    }

    return job
  }

  /**
   * Used for updating job events.
   *
   * @param id
   */
  async updateJobEvents(id: string) {
    const jobEventsPromise = this.api.jobEventsV3
      .list(new ListJobEventsRequest().setJobId(Id.assert(id)).setPageSize(500))
      .then((result) =>
        this.jobEvents.set(
          result
            .getJobEventsList()
            .map((x) => mapJobEventProtoToJobEventDto(x.toObject())),
        ),
      )

    const jobPromise = this._fetchJob(id).then(this.jobs.set)

    const [, job] = await Promise.all([jobEventsPromise, jobPromise])

    return job
  }
}

function mapConsultationIncompleteReasonFromPayload(
  payload: ConsultationIncompletePayload,
) {
  switch (payload.reason) {
    case ConsultationIncompleteReason.ConsultationNotNeeded:
      return ConsultationIncompleteReasonProto.CONSULTATION_UNNECESSARY
    case ConsultationIncompleteReason.ClientMissedCall:
      return ConsultationIncompleteReasonProto.CLIENT_MISSED_CALL
    case ConsultationIncompleteReason.ProMissedCall:
      return ConsultationIncompleteReasonProto.PRO_MISSED_CALL
    case ConsultationIncompleteReason.PreviousProCompletedCall:
      return ConsultationIncompleteReasonProto.PREVIOUS_PRO_COMPLETED_CALL
    case ConsultationIncompleteReason.ConsultationHandledViaChat:
      return ConsultationIncompleteReasonProto.COMPLETED_VIA_CHAT
    case ConsultationIncompleteReason.Other:
      return payload.otherReason
    default:
      return ConsultationIncompleteReasonProto.REASON_UNSPECIFIED
  }
}

export interface JobUpdatedEvent {
  type: 'JobUpdated'
  job: Job
}

export interface AllocationInput {
  user: User
  allocationAmount: number
  authorizedPartialPayoutAmount: number
}

/**
 * Params for submitting corrections.
 */
export interface SubmitCorrectionsParams {
  reason: string
  answers: AnswerDTO[]
}

/**
 * Params for updating a Draft Job.
 */
export interface UpdateDraftParams {
  name?: string
  answers?: AnswerDTO[]
  requestedProId?: Id | null
}

/**
 * Params for updating the Legend Version of a draft job.
 */
export interface UpdateLegendVersionParams {
  legendVersionId: LegendVersionId
}

/**
 * Params for submitting a request.
 */
export interface SubmitRequestParams {
  paymentMethod: BillingMethod | null
  bypassBilling?: boolean
}

/**
 * Params for requesting job scope changes.
 */
export interface ScopeChangeParams {
  name: string
  quantity: number
}

export interface ScopeChangeInventoryParams {
  versionId: string
  quantity: number
}

export interface AdminJobScopeUpdateCustomItemToCreateParams {
  name: string
  quantity: number
  price: number
  proPrice: number
}

export interface AdminJobScopeUpdateInventoryItemParams {
  versionId: string
  quantity: number
}

export interface AdminJobScopeUpdateCustomItemToRemoveParams {
  custom_item_id: string
}

export interface AdminJobScopeUpdatedEvent {
  type: 'AdminJobScopeUpdated'
  jobId: string
}

export interface JobScopeChangeApprovedEvent {
  type: 'JobScopeChangeApproved'
  jobId: string
}

export interface ListJobEventsQuery extends CursorPageQuery {
  job_id: string
}

/**
 * Maps a bulk transfer response type to the DTO type.
 */
function mapBulkTransferProtoResponseToDto(
  bulkResponse: BulkTransferJobsResponse.AsObject,
) {
  return bulkResponse.errorsList.map((e) => ({
    jobId: e.jobid,
    error: e.error,
  }))
}

/**
 * Maps a job event proto type to the DTO type.
 */
function mapJobEventProtoToJobEventDto(
  jobEventProto: JobEventProto.AsObject,
): JobEventDTO {
  return {
    id: jobEventProto.id,
    date_created: timestampToISO(jobEventProto.dateCreated)!,
    event_type: mapEventType(jobEventProto.eventType),
    event_visibility: mapEventVisibility(jobEventProto.eventVisibility),
    job_id: jobEventProto.jobId,
    description: jobEventProto.description,
    reason: jobEventProto.reason?.value,
    task_title: jobEventProto.taskTitle?.value,
    previous_provider: mapPreviousProvider(jobEventProto.previousProvider),
    new_team: mapNewTeam(jobEventProto.newTeam),
    job_resolution: mapJobResolution(jobEventProto.jobResolution),
    new_provider: mapNewProvider(jobEventProto.newProvider),
    new_client: mapNewClient(jobEventProto.newClient),
    provider: mapProvider(jobEventProto.provider),
    triggered_by: mapTriggeredBy(jobEventProto.triggeredBy!),
  }

  function mapEventType(eventType: JobEventProto.EventType): JobEventType {
    // prettier-ignore
    return match(eventType, {
      [JobEventProto.EventType.EVENT_TYPE_UNSPECIFIED]: () => 'UNSPECIFIED',
      [JobEventProto.EventType.UNCLAIMED]: () => 'UNCLAIMED',
      [JobEventProto.EventType.CLAIMED]: () => 'CLAIMED',
      [JobEventProto.EventType.UPDATED]: () => 'UPDATED',
      [JobEventProto.EventType.AMENDED]: () => 'AMENDED',
      [JobEventProto.EventType.REOPENED]: () => 'REOPENED',
      [JobEventProto.EventType.TRANSFERRED]: () => 'TRANSFERRED',
      [JobEventProto.EventType.FORFEITED]: () => 'FORFEITED',
      [JobEventProto.EventType.PROVIDER_ADDED]: () => 'PROVIDER_ADDED',
      [JobEventProto.EventType.PROVIDER_REMOVED]: () => 'PROVIDER_REMOVED',
      [JobEventProto.EventType.JOB_MEMBER_ADDED]: () => 'JOB_MEMBER_ADDED',
      [JobEventProto.EventType.JOB_MEMBER_REMOVED]: () => 'JOB_MEMBER_REMOVED',
      [JobEventProto.EventType.TASK_MARKED_COMPLETE]: () => 'TASK_MARKED_COMPLETE',
      [JobEventProto.EventType.TASK_MARKED_INCOMPLETE]: () => 'TASK_MARKED_INCOMPLETE',
      [JobEventProto.EventType.CLOSED]: () => 'CLOSED',
      [JobEventProto.EventType.RESUMED_JOB]: () => 'RESUMED_JOB',
      [JobEventProto.EventType.PUT_ON_HOLD]: () => 'PUT_ON_HOLD',
      [JobEventProto.EventType.BECAME_INACTIVE]: () => 'BECAME_INACTIVE',
      [JobEventProto.EventType.PAYMENT_INFO_REQUESTED]: () => 'PAYMENT_INFO_REQUESTED',
      [JobEventProto.EventType.PAYMENT_INFO_PROVIDED]: () => 'PAYMENT_INFO_PROVIDED',
      [JobEventProto.EventType.PAYMENT_SUCCESSFUL]: () => 'PAYMENT_SUCCESSFUL',
      [JobEventProto.EventType.PAYMENT_FAILED]: () => 'PAYMENT_FAILED',
      [JobEventProto.EventType.COUPON_ADDED]: () => 'COUPON_ADDED',
      [JobEventProto.EventType.COUPON_REMOVED]: () => 'COUPON_REMOVED',
      [JobEventProto.EventType.REFUND_SUCCESSFUL]: () => 'REFUND_SUCCESSFUL',
      [JobEventProto.EventType.REFUND_FAILED]: () => 'REFUND_FAILED',
      [JobEventProto.EventType.MANUAL_INVOICE_ADDED]: () => 'MANUAL_INVOICE_ADDED',
      [JobEventProto.EventType.ANSWERS_CORRECTED]: () => 'ANSWERS_CORRECTED',
      [JobEventProto.EventType.INFO_GATHERING_COMPLETED]: () => 'INFO_GATHERING_COMPLETED',
      [JobEventProto.EventType.CLIENT_ADDED]: () => 'CLIENT_ADDED',
      [JobEventProto.EventType.BECAME_ACTIVE]: () => 'UNSPECIFIED' as JobEventType,
      [match.DEFAULT]: () => 'UNSPECIFIED' as JobEventType
    })
  }

  function mapEventVisibility(
    eventVisibility: JobEventProto.EventVisibility,
  ): JobEventVisibility {
    return match(eventVisibility, {
      [JobEventProto.EventVisibility.PUBLIC]: () => 'PUBLIC',
      [JobEventProto.EventVisibility.INTERNAL]: () => 'INTERNAL',
    })
  }

  function mapPreviousProvider(
    previousProvider?: JobEventProto.Collaborator.AsObject,
  ) {
    return previousProvider
      ? { display_name: previousProvider.displayName }
      : undefined
  }

  function mapNewTeam(newTeam?: JobEventProto.Team.AsObject) {
    return newTeam ? { name: newTeam.name } : undefined
  }

  function mapJobResolution(resolution: JobEventProto.JobResolution) {
    const mappedResolution: 'DONE' | 'CANCELLED' | 'TEST' | null = match(
      resolution,
      {
        [JobEventProto.JobResolution.DONE]: () => 'DONE',
        [JobEventProto.JobResolution.CANCELLED]: () => 'CANCELLED',
        [JobEventProto.JobResolution.TEST]: () => 'TEST',
        [JobEventProto.JobResolution.JOB_RESOLUTION_UNSPECIFIED]: () => null,
      },
    )

    return mappedResolution ? { resolution: mappedResolution } : undefined
  }

  function mapNewProvider(newProvider?: JobEventProto.Collaborator.AsObject) {
    return newProvider ? { display_name: newProvider.displayName } : undefined
  }

  function mapNewClient(newClient?: JobEventProto.Collaborator.AsObject) {
    return newClient ? { display_name: newClient.displayName } : undefined
  }

  function mapProvider(provider?: JobEventProto.Collaborator.AsObject) {
    return provider ? { display_name: provider.displayName } : undefined
  }

  function mapTriggeredBy(triggeredBy: JobEventProto.TriggeredBy.AsObject) {
    return {
      user_id: triggeredBy.userId,
      user_public_id: triggeredBy.userPublicId,
      first_name: triggeredBy.givenName,
      last_name: triggeredBy.familyName,
    }
  }
}
